/**
 * HistoryManager - Wraps UndoRedoManager for the new Konva canvas architecture
 * Provides undo/redo functionality with command pattern
 */

import { EventEmitter } from '../core/EventEmitter';
import { EditableElement } from '../editing/types';
import {
  UndoRedoManager,
  Command,
  AddElementCommand,
  DeleteElementsCommand,
  ModifyElementCommand,
  MoveElementsCommand,
  BatchCommand,
} from '../editing/UndoRedoManager';

export interface HistoryManagerOptions {
  maxHistorySize?: number;
}

const DEFAULT_OPTIONS: Required<HistoryManagerOptions> = {
  maxHistorySize: 50,
};

export class HistoryManager {
  private undoRedoManager: UndoRedoManager;
  private emitter: EventEmitter;
  private options: Required<HistoryManagerOptions>;
  
  // Reference to elements setter for commands
  private elements: EditableElement[] = [];
  private setElements: ((elements: EditableElement[]) => void) | null = null;

  constructor(emitter: EventEmitter, options: HistoryManagerOptions = {}) {
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.undoRedoManager = new UndoRedoManager(this.options.maxHistorySize);
  }

  /**
   * Set elements reference and setter for commands
   */
  setElementsRef(elements: EditableElement[], setElements: (elements: EditableElement[]) => void): void {
    this.elements = elements;
    // Wrap setElements to also update this.elements immediately
    // This ensures subsequent commands use the updated elements array
    this.setElements = (newElements: EditableElement[]) => {
      this.elements = newElements;
      setElements(newElements);
    };
  }

  /**
   * Update elements reference
   */
  updateElementsRef(elements: EditableElement[]): void {
    this.elements = elements;
  }

  /**
   * Execute an add element command
   */
  addElement(element: EditableElement): void {
    if (!this.setElements) return;
    
    const cmd = new AddElementCommand(this.elements, element, this.setElements);
    this.undoRedoManager.execute(cmd);
  }

  /**
   * Execute a delete elements command
   */
  deleteElements(elementIds: string[]): void {
    if (!this.setElements) return;
    
    const cmd = new DeleteElementsCommand(this.elements, elementIds, this.setElements);
    this.undoRedoManager.execute(cmd);
  }

  /**
   * Execute a modify element command
   */
  modifyElement(elementId: string, updates: Partial<EditableElement>): void {
    if (!this.setElements) return;
    
    const cmd = new ModifyElementCommand(this.elements, elementId, updates, this.setElements);
    this.undoRedoManager.execute(cmd);
  }

  /**
   * Execute a move elements command
   */
  moveElements(updates: Map<string, { x: number; y: number }>): void {
    if (!this.setElements) return;
    
    const cmd = new MoveElementsCommand(this.elements, updates, this.setElements);
    this.undoRedoManager.execute(cmd);
  }

  /**
   * Execute a batch command
   */
  batch(commands: Command[], description?: string): void {
    const cmd = new BatchCommand(commands, description);
    this.undoRedoManager.execute(cmd);
  }

  /**
   * Execute a custom command
   */
  execute(command: Command): void {
    this.undoRedoManager.execute(command);
  }

  /**
   * Record a position/transform change without triggering setElements
   * Use this when the visual change has already been applied to Konva directly
   */
  recordPositionChange(
    elementId: string, 
    oldPosition: { x: number; y: number; width?: number; height?: number; rotation?: number },
    newPosition: { x: number; y: number; width?: number; height?: number; rotation?: number }
  ): void {
    if (!this.setElements) return;
    
    // Create a custom command that only updates state on undo/redo
    const cmd: Command = {
      description: 'Move/Transform element',
      execute: () => {
        // On execute (redo), apply the new position
        this.setElements!(
          this.elements.map(el => 
            el.id === elementId ? { ...el, ...newPosition } : el
          )
        );
      },
      undo: () => {
        // On undo, restore the old position
        this.setElements!(
          this.elements.map(el => 
            el.id === elementId ? { ...el, ...oldPosition } : el
          )
        );
      }
    };
    
    // Just push to undo stack WITHOUT executing (changes already applied to Konva)
    this.undoRedoManager.pushWithoutExecute(cmd);
  }

  /**
   * Undo the last action
   */
  undo(): boolean {
    return this.undoRedoManager.undo();
  }

  /**
   * Redo the last undone action
   */
  redo(): boolean {
    return this.undoRedoManager.redo();
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoRedoManager.canUndo();
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.undoRedoManager.canRedo();
  }

  /**
   * Clear history
   */
  clear(): void {
    this.undoRedoManager.clear();
  }

  /**
   * Get undo stack size
   */
  getUndoStackSize(): number {
    return this.undoRedoManager.getUndoStackSize();
  }

  /**
   * Get redo stack size
   */
  getRedoStackSize(): number {
    return this.undoRedoManager.getRedoStackSize();
  }

  /**
   * Get description of next undo command
   */
  getUndoDescription(): string | undefined {
    return this.undoRedoManager.getUndoDescription();
  }

  /**
   * Get description of next redo command
   */
  getRedoDescription(): string | undefined {
    return this.undoRedoManager.getRedoDescription();
  }

  /**
   * Get the underlying UndoRedoManager (for advanced use)
   */
  getManager(): UndoRedoManager {
    return this.undoRedoManager;
  }

  /**
   * Destroy the history manager
   */
  destroy(): void {
    this.undoRedoManager.clear();
    this.setElements = null;
    this.elements = [];
  }
}
